/**
 * EXIF GPS Extraction Service
 * Extracts GPS coordinates from photo metadata
 */

interface GPSCoordinates {
  latitude: number;
  longitude: number;
  altitude?: number;
  timestamp?: Date;
}

interface EXIFData {
  gps?: GPSCoordinates;
  datetime?: Date;
  make?: string;
  model?: string;
}

/**
 * Convert GPS DMS (degrees, minutes, seconds) to decimal degrees
 */
function dmsToDecimal(degrees: number, minutes: number, seconds: number, direction: string): number {
  let decimal = degrees + minutes / 60 + seconds / 3600;
  if (direction === 'S' || direction === 'W') {
    decimal = -decimal;
  }
  return decimal;
}

/**
 * Read EXIF data from an image file
 * Uses the browser's built-in capabilities where possible
 */
export async function extractEXIF(file: File): Promise<EXIFData> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;
        const exifData = parseEXIFFromBuffer(buffer);
        resolve(exifData);
      } catch (error) {
        console.warn('Failed to parse EXIF:', error);
        resolve({});
      }
    };
    
    reader.onerror = () => {
      console.warn('Failed to read file for EXIF');
      resolve({});
    };
    
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Parse EXIF data from ArrayBuffer
 * Simplified parser focusing on GPS data
 */
function parseEXIFFromBuffer(buffer: ArrayBuffer): EXIFData {
  const view = new DataView(buffer);
  const result: EXIFData = {};
  
  // Check for JPEG SOI marker
  if (view.getUint16(0) !== 0xFFD8) {
    return result;
  }
  
  let offset = 2;
  const length = view.byteLength;
  
  while (offset < length) {
    if (view.getUint8(offset) !== 0xFF) {
      offset++;
      continue;
    }
    
    const marker = view.getUint8(offset + 1);
    
    // APP1 marker (EXIF)
    if (marker === 0xE1) {
      const segmentLength = view.getUint16(offset + 2);
      const exifOffset = offset + 4;
      
      // Check for "Exif\0\0" header
      const exifHeader = String.fromCharCode(
        view.getUint8(exifOffset),
        view.getUint8(exifOffset + 1),
        view.getUint8(exifOffset + 2),
        view.getUint8(exifOffset + 3)
      );
      
      if (exifHeader === 'Exif') {
        const tiffOffset = exifOffset + 6;
        const gpsData = parseTIFFForGPS(view, tiffOffset);
        if (gpsData) {
          result.gps = gpsData;
        }
      }
      
      offset += 2 + segmentLength;
    } else if (marker === 0xD9) {
      // End of image
      break;
    } else if (marker >= 0xE0 && marker <= 0xEF) {
      // Other APP markers
      const segmentLength = view.getUint16(offset + 2);
      offset += 2 + segmentLength;
    } else {
      offset += 2;
    }
  }
  
  return result;
}

/**
 * Parse TIFF structure to find GPS IFD
 */
function parseTIFFForGPS(view: DataView, tiffStart: number): GPSCoordinates | undefined {
  try {
    // Check byte order
    const byteOrder = view.getUint16(tiffStart);
    const littleEndian = byteOrder === 0x4949; // "II"
    
    // Verify TIFF magic number
    const magic = view.getUint16(tiffStart + 2, littleEndian);
    if (magic !== 0x002A) {
      return undefined;
    }
    
    // Get offset to first IFD
    const ifdOffset = view.getUint32(tiffStart + 4, littleEndian);
    
    // Parse IFD0 to find GPS IFD pointer
    const gpsIFDOffset = findGPSIFD(view, tiffStart, tiffStart + ifdOffset, littleEndian);
    
    if (gpsIFDOffset) {
      return parseGPSIFD(view, tiffStart, gpsIFDOffset, littleEndian);
    }
    
    return undefined;
  } catch (error) {
    console.warn('Error parsing TIFF:', error);
    return undefined;
  }
}

/**
 * Find GPS IFD offset from IFD0
 */
function findGPSIFD(view: DataView, tiffStart: number, ifdStart: number, littleEndian: boolean): number | undefined {
  try {
    const entryCount = view.getUint16(ifdStart, littleEndian);
    
    for (let i = 0; i < entryCount; i++) {
      const entryOffset = ifdStart + 2 + (i * 12);
      const tag = view.getUint16(entryOffset, littleEndian);
      
      // GPS IFD pointer tag
      if (tag === 0x8825) {
        const gpsOffset = view.getUint32(entryOffset + 8, littleEndian);
        return tiffStart + gpsOffset;
      }
    }
    
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Parse GPS IFD to extract coordinates
 */
function parseGPSIFD(view: DataView, tiffStart: number, gpsStart: number, littleEndian: boolean): GPSCoordinates | undefined {
  try {
    const entryCount = view.getUint16(gpsStart, littleEndian);
    
    let latRef = 'N';
    let lonRef = 'E';
    let lat: number[] = [];
    let lon: number[] = [];
    let altitude: number | undefined;
    
    for (let i = 0; i < entryCount; i++) {
      const entryOffset = gpsStart + 2 + (i * 12);
      const tag = view.getUint16(entryOffset, littleEndian);
      // Skip type and count fields, we use valueOffset directly
      const valueOffset = view.getUint32(entryOffset + 8, littleEndian);
      
      switch (tag) {
        case 1: // GPSLatitudeRef
          latRef = String.fromCharCode(view.getUint8(entryOffset + 8));
          break;
        case 2: // GPSLatitude
          lat = readRationals(view, tiffStart + valueOffset, 3, littleEndian);
          break;
        case 3: // GPSLongitudeRef
          lonRef = String.fromCharCode(view.getUint8(entryOffset + 8));
          break;
        case 4: // GPSLongitude
          lon = readRationals(view, tiffStart + valueOffset, 3, littleEndian);
          break;
        case 6: // GPSAltitude
          altitude = readRational(view, tiffStart + valueOffset, littleEndian);
          break;
      }
    }
    
    if (lat.length === 3 && lon.length === 3) {
      return {
        latitude: dmsToDecimal(lat[0], lat[1], lat[2], latRef),
        longitude: dmsToDecimal(lon[0], lon[1], lon[2], lonRef),
        altitude
      };
    }
    
    return undefined;
  } catch (error) {
    console.warn('Error parsing GPS IFD:', error);
    return undefined;
  }
}

/**
 * Read rational number from EXIF data
 */
function readRational(view: DataView, offset: number, littleEndian: boolean): number {
  const numerator = view.getUint32(offset, littleEndian);
  const denominator = view.getUint32(offset + 4, littleEndian);
  return denominator ? numerator / denominator : 0;
}

/**
 * Read multiple rational numbers
 */
function readRationals(view: DataView, offset: number, count: number, littleEndian: boolean): number[] {
  const result: number[] = [];
  for (let i = 0; i < count; i++) {
    result.push(readRational(view, offset + (i * 8), littleEndian));
  }
  return result;
}

/**
 * Extract GPS coordinates from an image file
 * Returns null if no GPS data found
 */
export async function extractGPSFromImage(file: File): Promise<GPSCoordinates | null> {
  const exif = await extractEXIF(file);
  return exif.gps || null;
}

/**
 * Check if a file is a supported image type
 */
export function isSupportedImageType(file: File): boolean {
  const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
  return supportedTypes.includes(file.type.toLowerCase());
}

/**
 * Compress image for upload (max 5MB, reasonable dimensions)
 */
export async function compressImageForUpload(file: File, maxSizeMB: number = 5): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      // Max dimensions
      const MAX_WIDTH = 2048;
      const MAX_HEIGHT = 2048;
      
      let { width, height } = img;
      
      // Scale down if needed
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);
      
      // Start with high quality and reduce if needed
      let quality = 0.9;
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            
            const sizeMB = blob.size / (1024 * 1024);
            if (sizeMB > maxSizeMB && quality > 0.3) {
              quality -= 0.1;
              tryCompress();
            } else {
              resolve(blob);
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      tryCompress();
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Create thumbnail for preview
 */
export async function createThumbnail(file: File, maxSize: number = 300): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      let { width, height } = img;
      
      const ratio = Math.min(maxSize / width, maxSize / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
      
      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create thumbnail'));
          }
        },
        'image/jpeg',
        0.8
      );
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}
